import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, GitBranch } from 'lucide-react';
import { vdRoutingApi, vdDepartmentApi, vdDirectoryApi } from '../api';

const TRIGGER_TYPES = [
  { value: 'keyword', label: 'Spoken Keyword' },
  { value: 'keypad', label: 'Keypad Press' },
  { value: 'extension', label: 'Extension Dial' },
  { value: 'name_match', label: 'Name Match' },
];

const DEST_TYPES = [
  { value: 'department', label: 'Department' },
  { value: 'employee', label: 'Employee' },
  { value: 'external', label: 'External Number' },
  { value: 'voicemail', label: 'Voicemail' },
];

export default function VDRouting() {
  const [rules, setRules] = useState([]);
  const [depts, setDepts] = useState([]);
  const [emps, setEmps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { register, handleSubmit, reset, watch, setValue } = useForm();

  const load = () => Promise.all([vdRoutingApi.list(), vdDepartmentApi.list(), vdDirectoryApi.list()])
    .then(([r, d, e]) => { setRules(r); setDepts(d); setEmps(e); setLoading(false); });
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    reset({ label: '', triggerType: 'keyword', triggerValue: '', destinationType: 'department', destinationValue: '', priority: rules.length + 1, active: true });
    setDialogOpen(true);
  };
  const openEdit = (rule) => { setEditing(rule); reset(rule); setDialogOpen(true); };

  const onSubmit = async (data) => {
    data.priority = parseInt(data.priority) || 1;
    if (editing) {
      await vdRoutingApi.update(editing.id, data);
      toast.success('Rule updated');
    } else {
      await vdRoutingApi.create(data);
      toast.success('Rule added');
    }
    setDialogOpen(false);
    load();
  };

  const onDelete = async (id) => {
    await vdRoutingApi.remove(id);
    toast.success('Rule removed');
    load();
  };

  const triggerLabel = (t) => TRIGGER_TYPES.find(x => x.value === t)?.label || t;
  const destLabel = (t) => DEST_TYPES.find(x => x.value === t)?.label || t;

  return (
    <div className="max-w-4xl space-y-6" data-testid="vd-routing">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Call Routing</h1>
          <p className="text-muted-foreground text-sm mt-1">Define how incoming calls are routed</p>
        </div>
        <Button onClick={openNew} className="bg-emerald-500 hover:bg-emerald-600 text-black font-medium" data-testid="add-rule-btn">
          <Plus className="w-4 h-4 mr-1.5" />Add Rule
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2].map(i => <div key={i} className="h-20 bg-card/5 rounded-xl animate-pulse" />)}</div>
      ) : rules.length === 0 ? (
        <Card className="bg-card/5 border-white/10">
          <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <GitBranch className="w-10 h-10 mb-3 opacity-40" />
            <p className="text-sm">No routing rules yet</p>
            <Button variant="outline" size="sm" className="mt-3 border-white/10" onClick={openNew}>Create first rule</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {rules.map((rule, i) => (
            <Card key={rule.id} className="bg-card/5 border-white/10">
              <CardContent className="flex items-center justify-between py-3 px-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-8 h-8 rounded bg-orange-500/10 flex items-center justify-center text-orange-400 text-xs font-bold shrink-0">
                    {rule.priority}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-sm">{rule.label}</div>
                    <div className="text-xs text-muted-foreground">
                      {triggerLabel(rule.triggerType)}: <span className="text-foreground">{rule.triggerValue}</span>
                      {' → '}{destLabel(rule.destinationType)}: <span className="text-foreground">{rule.destinationValue}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={rule.active ? 'border-emerald-500/30 text-emerald-400' : 'border-border text-muted-foreground'}>{rule.active ? 'On' : 'Off'}</Badge>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(rule)} className="text-muted-foreground hover:text-foreground h-8 w-8"><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => onDelete(rule.id)} className="text-muted-foreground hover:text-red-400 h-8 w-8"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#1a1d2e] border-white/10 text-foreground sm:max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Routing Rule</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div><Label>Label</Label><Input {...register('label', { required: true })} className="bg-card/5 border-white/10 mt-1" placeholder="e.g. Route to Sales" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Trigger Type</Label>
                <Select value={watch('triggerType') || 'keyword'} onValueChange={v => setValue('triggerType', v)}>
                  <SelectTrigger className="bg-card/5 border-white/10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1a1d2e] border-white/10 text-foreground">
                    {TRIGGER_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Trigger Value</Label><Input {...register('triggerValue')} className="bg-card/5 border-white/10 mt-1" placeholder="sales, 1, 100..." /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Destination Type</Label>
                <Select value={watch('destinationType') || 'department'} onValueChange={v => setValue('destinationType', v)}>
                  <SelectTrigger className="bg-card/5 border-white/10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1a1d2e] border-white/10 text-foreground">
                    {DEST_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Destination</Label><Input {...register('destinationValue')} className="bg-card/5 border-white/10 mt-1" placeholder="Dept name, ext, number..." /></div>
            </div>
            <div><Label>Priority</Label><Input type="number" {...register('priority')} className="bg-card/5 border-white/10 mt-1 w-24" /></div>
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch checked={!!watch('active')} onCheckedChange={v => setValue('active', v)} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10">Cancel</Button>
              <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600 text-black">{editing ? 'Save' : 'Add'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
